import {AutoComplete} from '@/components/SendNotification/AutoComplete'
import {CustomImageSelector} from '@/components/SendNotification/CustomImageSelector'
import {LandingPageSelector} from '@/components/SendNotification/LandingPageSelector'
import {ImageUploadChangeValue} from '@/components/SingleImageUpload'
import {STEP_CONFIG} from '@/config/constants'
import {useCountries} from '@/hooks/useCountries'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'
import {PushStepType} from '@/types/NotificationStep'
import {GetSendPushStepConfigResponse, LandingPageItem} from '@/types/StepConfigParams'
import {getAllDynamicTagsRtnUseCase} from '@/useCases/getAllTagsUseCase'
import {getStepConfigUseCase} from '@/useCases/getStepConfigUseCase'
import {MAX_MESSAGE_LENGTH, MAX_PUSH_MESSAGE_LENGTH} from '@/utils/consts'
import {useQuery} from '@tanstack/react-query'
import {useEffect} from 'react'
import {useIntl} from 'react-intl'

interface Props {
  pushStep: PushStepType
  setPushStep: (pushStep: PushStepType) => void
}

export default function SendPushParams({pushStep, setPushStep}: Props) {
  const {formatMessage} = useIntl()
  // const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  // const [selectedPage, setSelectedPage] = useState<string | null>(null)

  const {country} = useCountries()

  const handlePageChange = (selectedLandingPage: LandingPageItem) => {
    // const selectedPageItem = landingPageOptions.find(page => page.id === selectedLandingPage.id)
    setPushStep({...pushStep, selectedPage: selectedLandingPage})
  }

  const handleImageChange = (image: ImageUploadChangeValue) => {
    const selectedImage = typeof image === 'string' ? {id: '', url: image, title: null} : image
    setPushStep({...pushStep, selectedImage})
  }

  const {
    isFetching: isFetchingTags,
    data: dataTags,
    error: errorTags,
  } = useQuery({
    queryKey: ['dynamicTagsRtn', ['dynamicTagsRtn']],
    queryFn: ({signal}) => getAllDynamicTagsRtnUseCase({}, signal),
  })

  const {
    isFetching: isStepConfigFetching,
    data: stepConfigData,
    error: stepConfigError,
  } = useQuery({
    queryKey: [STEP_CONFIG.DATA_QUERY_KEY, NodeTypeEnum.sendPush, country],
    queryFn: ({signal}: {signal: AbortSignal}) =>
      getStepConfigUseCase(NodeTypeEnum.sendPush, signal) as GetSendPushStepConfigResponse,

    staleTime: 3 * 60 * 1000, // 3 minutes cache
  })

  const dynamicTagSuggestions = (dataTags?.data.messageParameters ?? [])
    .filter(item => item.active)
    .map(item => item.name)
  const landingPageOptions = stepConfigData?.landingPages ?? []
  const defaultLandingPage = landingPageOptions.find(option => option.isDefault)

  useEffect(() => {
    if (!pushStep.selectedPage && defaultLandingPage) {
      setPushStep({...pushStep, selectedPage: defaultLandingPage})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLandingPage])

  return (
    <div className="h-42 flex w-full max-w-xs flex-col gap-3" data-testid="send-push-params">
      {(isFetchingTags || isStepConfigFetching) && <div>Data is Loading...</div>}
      {!(isFetchingTags || isStepConfigFetching) && !(errorTags || stepConfigError) && (
        <>
          <AutoComplete
            label={formatMessage({id: 'NOTIFICATION_BUILDER.SUBJECT_LABEL'})}
            inputId="notification-subject"
            inputValue={pushStep.subject}
            onChangeInput={(value: string) => setPushStep({...pushStep, subject: value})}
            max_message_length={MAX_MESSAGE_LENGTH}
            dynamicTagSuggestions={dynamicTagSuggestions}
            tagOffset={-100}
            required
          />
          <AutoComplete
            label={formatMessage({id: 'NOTIFICATION_BUILDER.MESSAGE_LABEL'})}
            inputId="notification-message"
            inputValue={pushStep.message}
            onChangeInput={(value: string) => setPushStep({...pushStep, message: value})}
            max_message_length={MAX_PUSH_MESSAGE_LENGTH}
            dynamicTagSuggestions={dynamicTagSuggestions}
            tagOffset={-100}
            required
          />
          <LandingPageSelector
            label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_LANDING_PAGE'})}
            options={landingPageOptions}
            value={pushStep.selectedPage || defaultLandingPage}
            onChange={handlePageChange}
            disabled={false}
          />
          <CustomImageSelector
            label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_PUSH_IMAGE'})}
            value={pushStep.selectedImage?.url}
            onChange={handleImageChange}
            disabled={false}
            required
          />
        </>
      )}
    </div>
  )
}
